import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { useMasterData } from "../../Common/commonEvents";
import dayjs from "dayjs";
import { formatSlotTime } from "../../../Utils/dateUtils";

export default function ConfirmAppointmentModal({
    open,
    onClose,
    onConfirm,
    appointment,
}) {
    const [sessionUserData, setSessionUserData] = useState([]);
    const [doctorId, setDoctorId] = useState("");
    const [timeSlot, setTimeSlot] = useState("");

    useEffect(() => {
        const userData = JSON.parse(sessionStorage.getItem("userData"));

        if (userData) {
            setSessionUserData(userData);
        }
    }, []);

    const {
        usersByRole,
        doctorSlotsByDate,
        fetchUsersByRole,
        fetchDoctorSlotsForDate,
    } = useMasterData();

    useEffect(() => {
        if (!sessionUserData?.OrgId) return;

        fetchUsersByRole(sessionUserData.OrgId, 2); // doctors
    }, [sessionUserData?.OrgId]);

    const filteredDoctors = (usersByRole || []).filter(
        (doctor) => doctor.UnitId == appointment?.UnitId
    );

    useEffect(() => {
        if (!sessionUserData?.OrgId || !doctorId) return;

        fetchDoctorSlotsForDate(sessionUserData.OrgId, doctorId, appointment?.AppointmentDate); // doctor slots
    }, [sessionUserData?.OrgId, doctorId]);

    const handleConfirm = () => {

        onConfirm?.({
            appointment,
            doctorId,
            timeSlot,
        });

        setTimeSlot("");
        onClose?.();
    };

    const handleClose = () => {
        setTimeSlot("");
        onClose?.();
    };

    const generateTimeSlots = (
        startTime,
        endTime,
        duration
    ) => {

        const slots = [];

        const formatTimeOnly = (time) => {
            const extracted =
                time.split("T")[1].substring(0, 5);
            return dayjs(
                extracted,
                "HH:mm"
            );
        };

        let start = formatTimeOnly(startTime);
        let end = formatTimeOnly(endTime);

        // HANDLE NEXT DAY CASE
        if (end.isBefore(start)) {
            end = end.add(1, "day");
        }

        while (
            start.add(duration, "minute")
                .isBefore(end) ||

            start.add(duration, "minute")
                .isSame(end)
        ) {

            slots.push({
                label: start.format("hh:mm A"),

                value: start.format("HH:mm"),
            });

            start = start.add(
                duration,
                "minute"
            );
        }

        return slots;
    };


    const formatApiTime = (isoString, formatType) => {
        const timePart = isoString.split("T")[1].substring(0, 5); // "17:30"
        return dayjs(timePart, "HH:mm").format(formatType);
    };

    const availableSlots = (doctorSlotsByDate || [])
        .filter(slot => slot.IsBooked === 0 && slot.SlotStatus === "Available")
        .map(slot => ({
            label: formatApiTime(slot.SlotStart, "hh:mm A"), // Displays: "05:30 PM"
            value: formatApiTime(slot.SlotStart, "HH:mm"),   // Target dropdown key value: "17:30"
        }));

    return (
        <Modal
            title={null}
            open={open}
            onCancel={handleClose}
            footer={null}
            centered
            width={620}
            className="confirm-appointment-modal"
        >
            <div className="confirm-modal-header">
                <div className="confirm-modal-icon">
                    <i className="bi bi-calendar2-check text-white"></i>
                </div>

                <div>
                    <h3>Confirm Appointment</h3>
                    <p>Assign a doctor and choose an available slot</p>
                </div>
            </div>

            <div className="confirm-modal-body">
                <div className="selected-slot-card mb-3">
                    <div className="selected-slot-icon">
                        <i className="bi bi-clock-history text-white"></i>
                    </div>

                    <div>
                        <div className="selected-slot-label">Patient Selected Time</div>
                        <div className="selected-slot-value">
                            {appointment?.SlotTime &&
                                appointment.SlotTime !== "1970-01-01T00:00:00.000Z"
                                ? formatSlotTime(appointment.SlotTime)
                                : "No Slot Selected"}
                        </div>
                    </div>
                </div>
                <div className="row g-3">
                    <div className="col-12">
                        <label className="form-label fw-bold text-muted mb-2">
                            Select Doctor
                        </label>

                        <div className="input-icon-wrap">
                            <i className="bi bi-person-badge"></i>
                            <select
                                className="form-select form-select-lg"
                                value={doctorId}
                                onChange={(e) => setDoctorId(e.target.value)}
                            >
                                <option value="">Select Doctor</option>
                                {filteredDoctors?.map((doctor) => (
                                    <option key={doctor.Id} value={doctor.Id}>
                                        {doctor.Name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="col-12">
                        <label className="form-label premium-label mb-2">
                            Select Available Time Slot
                        </label>

                        <div className="premium-input-wrapper position-relative">
                            <span className="premium-input-icon">
                                <i className="bi bi-clock-history"></i>
                            </span>

                            <select
                                className="form-select premium-select"
                                value={timeSlot} // Evaluates to string keys like "17:30" or "18:45"
                                onChange={(e) => setTimeSlot(e.target.value)}
                            >
                                <option value="">Choose a slot...</option>

                                {availableSlots.length > 0 ? (
                                    availableSlots.map((slot, index) => (
                                        <option key={index} value={slot.value}>
                                            {slot.label}
                                        </option>
                                    ))
                                ) : (
                                    <option value="" disabled>
                                        ✕ No appointment slots available for this day
                                    </option>
                                )}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="confirm-modal-footer">
                <button
                    type="button"
                    className="btn btn-light fw-bold px-4"
                    onClick={handleClose}
                >
                    <i className="bi bi-x-circle me-2"></i>
                    Close
                </button>

                <button
                    type="button"
                    className="btn btn-primary fw-bold px-4"
                    onClick={handleConfirm}
                    disabled={!doctorId || !timeSlot}
                >
                    <i className="bi bi-check2-circle me-2"></i>
                    Confirm Appointment
                </button>
            </div>

            <style>
                {`
                /* Custom Label Styling */
                    .premium-label {
                        font-size: 0.85rem;
                        font-weight: 600;
                        color: #475569; /* Slate 600 */
                        letter-spacing: 0.02em;
                        text-transform: uppercase;
                    }

                    /* Container Wrapper */
                    .premium-input-wrapper {
                        display: flex;
                        align-items: center;
                    }

                    /* Left-aligned Icon Positioning */
                    .premium-input-icon {
                        position: absolute;
                        left: 14px;
                        color: #94a3b8; /* Soft slate icon tint */
                        font-size: 1.1rem;
                        pointer-events: none;
                        z-index: 4;
                    }

                    /* Premium Select Box Restyle */
                    .premium-select {
                        padding-left: 2.75rem !important; /* Make clear space for the absolute icon */
                        padding-top: 0.65rem !important;
                        padding-bottom: 0.65rem !important;
                        font-size: 0.95rem !important;
                        color: #0f172a !important; /* Deep contrast dark text */
                        background-color: #ffffff !important;
                        border: 1px solid #e2e8f0 !important; /* Crisp subtle borders */
                        border-radius: 8px !important;
                        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02) !important;
                        transition: all 0.2s ease-in-out !important;
                        cursor: pointer;
                    }

                    /* Premium Focus Ring Adjustments */
                    .premium-select:focus {
                        border-color: #0f172a !important; /* Matches premium dark primary theme */
                        box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08) !important; /* Soft dark focus halo */
                        outline: none;
                    }

                    /* Disabled styling state */
                    .premium-select:disabled {
                        background-color: #f8fafc !important;
                        color: #94a3b8 !important;
                        border-color: #e2e8f0 !important;
                    }
                .selected-slot-card {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 14px 16px;
                    border-radius: 14px;
                    background: linear-gradient(135deg, #eef7ff, #f8fbff);
                    border: 1px solid #d8ecff;
                    box-shadow: 0 10px 28px rgba(13, 110, 253, 0.08);
                }

                .selected-slot-icon {
                    width: 46px;
                    height: 46px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #0d6efd;
                    color: #fff;
                    font-size: 22px;
                    box-shadow: 0 8px 18px rgba(13, 110, 253, 0.28);
                }

                .selected-slot-label {
                    font-size: 12px;
                    font-weight: 700;
                    color: #6c757d;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                    margin-bottom: 2px;
                }

                .selected-slot-value {
                    font-size: 22px;
                    font-weight: 800;
                    color: #172033;
                    line-height: 1.1;
                }
                .confirm-appointment-modal .ant-modal-content {
                border-radius: 20px;
                overflow: hidden;
                padding: 0;
                }

                .confirm-modal-header {
                padding: 24px;
                background: linear-gradient(135deg, #0f766e, #2563eb);
                color: #fff;
                display: flex;
                align-items: center;
                gap: 16px;
                }

                .confirm-modal-icon {
                width: 54px;
                height: 54px;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 26px;
                }

                .confirm-modal-header h3 {
                margin: 0;
                font-size: 22px;
                font-weight: 900;
                color: #fff;
                }

                .confirm-modal-header p {
                margin: 4px 0 0;
                color: rgba(255, 255, 255, 0.78);
                font-weight: 600;
                }

                .confirm-modal-body {
                padding: 24px;
                background: #f8fafc;
                }

                .input-icon-wrap {
                position: relative;
                }

                .input-icon-wrap > i {
                position: absolute;
                left: 14px;
                top: 50%;
                transform: translateY(-50%);
                color: #2563eb;
                font-size: 18px;
                z-index: 2;
                }

                .input-icon-wrap .form-select {
                padding-left: 44px;
                border-radius: 14px;
                border-color: #e5e7eb;
                font-size: 14px;
                font-weight: 700;
                min-height: 46px;
                }

                .confirm-modal-footer {
                padding: 16px 24px;
                background: #fff;
                border-top: 1px solid #eef2f7;
                display: flex;
                justify-content: flex-end;
                gap: 10px;
                }
            `}
            </style>
        </Modal>
    );
}
